import React, { useEffect, useMemo, useState } from 'react'
import {
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Modal,
  Pagination,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import SearchIcon from '@mui/icons-material/Search'
import TuneIcon from '@mui/icons-material/Tune'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import { TrashIcon } from '../../components/icons/TrashIcon'
import { navigate } from '@reach/router'
import { useTranslation } from 'react-i18next'
import { getProtocolContainer } from '../../container/protocol-module'
import { IProtocolService, PROTOCOL_SERVICE_KEY } from '../../modules/protocols'
import { Protocol, ProtocolStatus } from '../../modules/protocols/models/Protocol'
import { getUserContainer } from '../../container/user-module'
import { IUserService, USER_SERVICE_KEY } from '../../modules/users'
import { User } from '../../modules/users/models/User'
import { Query } from '../../common/api/Query'
import { ROUTE_PROTOCOLS } from '../../routes/routes-constants'
import { PageLayout } from 'pages/layout'
import { AppTable } from '../../components/table'
import { Field } from '../../components/table/types'
import { ImportProtocol } from './ImportProtocol'
import { CustomModal } from '../../components/modal/CustomModal'

const protocolService = getProtocolContainer().get<IProtocolService>(PROTOCOL_SERVICE_KEY)
const userService = getUserContainer().get<IUserService>(USER_SERVICE_KEY)

const ROWS_PER_PAGE = 5

// The design shows the badge as "Approved", but the backend vocabulary is
// "published". Only the label is mapped: the enum is left untouched so the API
// contract keeps matching.
const STATUS_LABEL: Record<ProtocolStatus, string> = {
  [ProtocolStatus.Draft]: 'statusDraft',
  [ProtocolStatus.Pending]: 'statusPending',
  [ProtocolStatus.Published]: 'statusApproved',
  [ProtocolStatus.Retired]: 'statusRetired',
}

const STATUS_STYLE: Record<ProtocolStatus, { bg: string; color: string }> = {
  [ProtocolStatus.Draft]: { bg: 'var(--gray-soft)', color: 'var(--gray-medium)' },
  [ProtocolStatus.Pending]: { bg: 'rgba(214, 162, 67, 0.16)', color: 'var(--warning)' },
  [ProtocolStatus.Published]: { bg: 'rgba(88, 169, 66, 0.16)', color: 'var(--success)' },
  [ProtocolStatus.Retired]: { bg: 'var(--gray-soft)', color: 'var(--gray-text)' },
}

function StatusBadge({ status }: { status: ProtocolStatus }) {
  const { t } = useTranslation()
  const s = STATUS_STYLE[status]

  return (
    <Box
      component="span"
      sx={{
        display: 'inline-block',
        px: 2,
        py: 0.5,
        borderRadius: '12px',
        fontSize: '13px',
        backgroundColor: s.bg,
        color: s.color,
      }}
    >
      {t(STATUS_LABEL[status])}
    </Box>
  )
}

export function ProtocolList() {
  const { t } = useTranslation()

  const [protocols, setProtocols] = useState<Protocol[]>([])
  const [authors, setAuthors] = useState<User[]>([])
  const [status, setStatus] = useState<ProtocolStatus | ''>('')
  const [search, setSearch] = useState<string>('')
  const [page, setPage] = useState<number>(0)
  const [reload, setReload] = useState<number>(0)
  const [isLoading, setIsLoading] = useState<boolean>(true)

  const [filterAnchor, setFilterAnchor] = useState<null | HTMLElement>(null)
  const [rowMenu, setRowMenu] = useState<{ anchor: HTMLElement; protocol: Protocol } | null>(null)
  const [createOpen, setCreateOpen] = useState<boolean>(false)
  const [name, setName] = useState<string>('')
  const [protocolToDelete, setProtocolToDelete] = useState<Protocol | undefined>()

  useEffect(() => {
    setIsLoading(true)
    protocolService.list(status || undefined).subscribe((l) => {
      setProtocols(l || [])
      setIsLoading(false)
    })
  }, [status, reload])

  // The list only carries approverID, so the names and initials shown in the
  // author column are resolved in a second call, same as the patients table.
  useEffect(() => {
    const ids = protocols.map((p) => p.approverID).filter(Boolean) as string[]
    if (!ids.length) {
      setAuthors([])
      return
    }
    userService
      .getFilteredList(
        new Query({ pager: { offset: 0, limit: ids.length }, query: [{ name: 'ids', value: ids }] })
      )
      .subscribe((res) => setAuthors(res.items))
  }, [protocols])

  // Searching and paging happen here because list() returns every protocol at
  // once: it takes neither a text filter nor a pager.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return q ? protocols.filter((p) => p.name.toLowerCase().includes(q)) : protocols
  }, [protocols, search])

  const paged = useMemo(
    () => filtered.slice(page * ROWS_PER_PAGE, page * ROWS_PER_PAGE + ROWS_PER_PAGE),
    [filtered, page]
  )

  // Mismo helper que la tabla de usuarios, para que las acciones se vean igual
  // en toda la app: iconos grises, la papelera en rojo. Antes aqui eran svg
  // verdes de otro juego de iconos.
  const accion = (
    Icono: React.ElementType,
    label: string,
    handler: () => void,
    opts: { rojo?: boolean } = {}
  ) => (
    <Tooltip title={t(label)}>
      <IconButton
        size="small"
        onClick={handler}
        sx={{ p: 0.5, color: opts.rojo ? 'var(--destructive, #e14c54)' : '#9b9d9a' }}
      >
        <Icono sx={{ fontSize: 20 }} />
      </IconButton>
    </Tooltip>
  )

  const remove = () => {
    if (!protocolToDelete) return
    protocolService.remove(protocolToDelete.id).subscribe(() => {
      setProtocolToDelete(undefined)
      setReload((n) => n + 1)
    })
  }

  const create = () => {
    if (!name.trim()) return
    protocolService.create(name.trim(), '').subscribe((p) => {
      setName('')
      setCreateOpen(false)
      if (p) navigate(`${ROUTE_PROTOCOLS}/${p.id}`).then()
    })
  }

  const transition = (
    id: string,
    action: 'submit' | 'approve' | 'reject' | 'retire' | 'newVersion'
  ) => {
    const fn = {
      submit: protocolService.submit.bind(protocolService),
      approve: protocolService.approve.bind(protocolService),
      reject: protocolService.reject.bind(protocolService),
      retire: protocolService.retire.bind(protocolService),
      newVersion: protocolService.newVersion.bind(protocolService),
    }[action]

    fn(id).subscribe(() => setReload((n) => n + 1))
    setRowMenu(null)
  }

  // The available actions depend on the status: it is the same state machine the
  // backend validates, mirrored here only so impossible actions are not offered.
  const transitionsFor = (p: Protocol) => {
    switch (p.status) {
      case ProtocolStatus.Draft:
        return [{ label: t('submitForApproval'), action: 'submit' as const }]
      case ProtocolStatus.Pending:
        return [
          { label: t('approve'), action: 'approve' as const },
          { label: t('reject'), action: 'reject' as const },
        ]
      case ProtocolStatus.Published:
        return [
          { label: t('newVersion'), action: 'newVersion' as const },
          { label: t('retire'), action: 'retire' as const },
        ]
      default:
        return []
    }
  }

  const authorOf = (p: Protocol) => authors.find((a) => a.id === p.approverID)

  const fields: Field<Protocol>[] = [
    {
      name: 'name',
      label: t('protocolName'),
      renderFunc: (f, p) => (
        <Typography
          sx={{ cursor: 'pointer', fontSize: '14px' }}
          onClick={() => navigate(`${ROUTE_PROTOCOLS}/${p.id}`)}
        >
          {p.name}
        </Typography>
      ),
    },
    { name: 'version', label: t('version'), renderFunc: (f, p) => `v. ${p.version}` },
    {
      name: 'updatedAt',
      label: t('lastModified'),
      // dd/MM/yyyy con ceros, como el diseño: es-ES a secas da "13/8/2026".
      renderFunc: (f, p) =>
        p.updatedAt
          ? new Date(p.updatedAt).toLocaleDateString('es-ES', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
            })
          : '',
    },
    {
      name: 'approverID',
      label: t('author'),
      renderFunc: (f, p) => {
        const a = authorOf(p)
        if (!a) {
          return (
            <Typography sx={{ fontSize: '14px', color: 'var(--gray-border)' }}>
              {t('noAuthor')}
            </Typography>
          )
        }
        return (
          <Box display="flex" alignItems="center" gap={1}>
            <Avatar sx={{ width: 28, height: 28, fontSize: '12px', bgcolor: 'var(--green-light)' }}>
              {`${a.firstName?.[0] ?? ''}${a.lastName?.[0] ?? ''}`.toUpperCase()}
            </Avatar>
            <Typography sx={{ fontSize: '14px' }}>{`${a.firstName} ${a.lastName}`}</Typography>
          </Box>
        )
      },
    },
    { name: 'status', label: t('status'), renderFunc: (f, p) => <StatusBadge status={p.status} /> },
    {
      // Not part of the model: it is the actions column the design puts last.
      // It is a normal field, not AppTable's `actions`, because the state
      // transitions need a menu and `actions` only takes icon + handler.
      name: 'id',
      label: t('actions'),
      renderFunc: (f, p) => {
        const transitions = transitionsFor(p)
        return (
          <Box display="flex" alignItems="center" gap={0.5}>
            {accion(VisibilityOutlinedIcon, 'see', () => navigate(`${ROUTE_PROTOCOLS}/${p.id}`))}
            {accion(TrashIcon, 'delete', () => setProtocolToDelete(p), { rojo: true })}
            {/* El hueco se reserva aunque no haya transiciones, para que la
                papelera no baile de sitio entre filas. */}
            {transitions.length > 0 ? (
              <Tooltip title={t('moreOptions')}>
                <IconButton
                  size="small"
                  onClick={(e) => setRowMenu({ anchor: e.currentTarget, protocol: p })}
                  sx={{ p: 0.5, color: '#9b9d9a' }}
                >
                  <MoreVertIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>
            ) : (
              <Box sx={{ width: 30 }} />
            )}
          </Box>
        )
      },
    },
  ]

  return (
    <PageLayout>
      {/* width al 100%: .innerCard de PageLayout centra a los hijos que no lo declaran */}
      <Box
        display="flex"
        alignItems="flex-start"
        justifyContent="space-between"
        width="100%"
        mb={3}
        gap={2}
      >
        <Box>
          <Typography sx={{ fontSize: '22px', fontWeight: 600, color: 'var(--gray-medium)' }}>
            {t('clinicalProtocols')}
          </Typography>
          <Typography sx={{ fontSize: '13px', color: 'var(--gray-text)' }}>
            {t('protocolsSubtitle')}
          </Typography>
        </Box>

        <Box display="flex" alignItems="center" gap={1.5}>
          <ImportProtocol onImported={() => setReload((n) => n + 1)} />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateOpen(true)}
            sx={{
              textTransform: 'none',
              borderRadius: 'var(--button-radius)',
              boxShadow: 'none',
              backgroundColor: 'var(--green-primary)',
              '&:hover': { backgroundColor: 'var(--green-dark)', boxShadow: 'none' },
            }}
          >
            {t('createNewProtocol')}
          </Button>
        </Box>
      </Box>

      <Box display="flex" alignItems="center" width="100%" gap={1.5} mb={2}>
        <TextField
          size="small"
          placeholder={t('searchProtocolsByName')}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value)
            setPage(0)
          }}
          sx={{ flexGrow: 1, maxWidth: '420px', '& fieldset': { borderRadius: '6px' } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: 'var(--gray-border)' }} />
              </InputAdornment>
            ),
          }}
        />
        <Button
          variant="outlined"
          startIcon={<TuneIcon />}
          onClick={(e) => setFilterAnchor(e.currentTarget)}
          sx={{
            textTransform: 'none',
            borderRadius: 'var(--button-radius)',
            borderColor: 'var(--gray-border)',
            color: 'var(--gray-text)',
            '&:hover': { borderColor: 'var(--green-primary)', backgroundColor: 'transparent' },
          }}
        >
          {t('filters')}
        </Button>
      </Box>

      <Menu
        anchorEl={filterAnchor}
        open={Boolean(filterAnchor)}
        onClose={() => setFilterAnchor(null)}
      >
        {[
          { value: '' as const, label: t('allStatuses') },
          { value: ProtocolStatus.Draft, label: t('statusDraft') },
          { value: ProtocolStatus.Pending, label: t('statusPending') },
          { value: ProtocolStatus.Published, label: t('statusApproved') },
          { value: ProtocolStatus.Retired, label: t('statusRetired') },
        ].map((o) => (
          <MenuItem
            key={o.value || 'all'}
            selected={status === o.value}
            onClick={() => {
              setStatus(o.value)
              setPage(0)
              setFilterAnchor(null)
            }}
          >
            {o.label}
          </MenuItem>
        ))}
      </Menu>

      <AppTable rowKeyField="id" items={paged} fields={fields} isLoading={isLoading} />

      {!isLoading && filtered.length === 0 && (
        <Box py={4} textAlign="center">
          <Typography color="text.secondary">{t('noProtocols')}</Typography>
        </Box>
      )}

      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        width="100%"
        gap={1}
        mt={2}
      >
        {' '}
        <Pagination
          count={Math.max(1, Math.ceil(filtered.length / ROWS_PER_PAGE))}
          page={page + 1}
          onChange={(_, p) => setPage(p - 1)}
          shape="rounded"
          sx={{
            '& .Mui-selected': {
              backgroundColor: 'var(--green-primary) !important',
              color: 'var(--white)',
            },
          }}
        />
      </Box>

      <Menu
        anchorEl={rowMenu?.anchor ?? null}
        open={Boolean(rowMenu)}
        onClose={() => setRowMenu(null)}
      >
        {rowMenu &&
          transitionsFor(rowMenu.protocol).map((a) => (
            <MenuItem key={a.action} onClick={() => transition(rowMenu.protocol.id, a.action)}>
              {a.label}
            </MenuItem>
          ))}
      </Menu>

      {/* Confirmacion antes de borrar, igual que en la tabla de usuarios: antes
          la papelera borraba el protocolo de golpe. */}
      <Modal open={Boolean(protocolToDelete)} onClose={() => setProtocolToDelete(undefined)}>
        <CustomModal
          title={t('deleteProtocol')}
          warningText={t('irreversibleDeleteProtocolAction')}
          destructive
          confirmLabel={t('yesDelete')}
          handleClose={() => setProtocolToDelete(undefined)}
          handleSave={remove}
        />
      </Modal>

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>{t('createNewProtocol')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            size="small"
            margin="dense"
            label={t('protocolName')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && create()}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} sx={{ textTransform: 'none' }}>
            {t('cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={create}
            disabled={!name.trim()}
            sx={{
              textTransform: 'none',
              boxShadow: 'none',
              backgroundColor: 'var(--green-primary)',
              '&:hover': { backgroundColor: 'var(--green-dark)', boxShadow: 'none' },
            }}
          >
            {t('createProtocol')}
          </Button>
        </DialogActions>
      </Dialog>
    </PageLayout>
  )
}
